import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { activitiesAPI } from '../api/client';
import { MapPin, Calendar, Clock, Users, Footprints, Coffee, Mountain, Bike, UtensilsCrossed, Landmark } from 'lucide-react';
import '../styles/activity-preview.css';

const ACTIVITY_ICONS = {
  run: Footprints, coffee: Coffee, hike: Mountain,
  cycling: Bike, dinner: UtensilsCrossed, museum: Landmark, other: MapPin,
};

function ActivityIcon({ type }) {
  const Icon = ACTIVITY_ICONS[type] || MapPin;
  return <Icon size={22} color="#52AAD8" strokeWidth={2} />;
}

function formatDate(str) {
  if (!str) return 'Flexible timing';
  return new Date(str).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : name[0]).toUpperCase();
}

export default function ActivityPreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    activitiesAPI.getById(id)
      .then(r => setActivity(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const going = (activity?.rsvps || []).filter(r => r.response_type === 'im_in');

  const registerUrl = `/register?redirect=/activity/${id}&intent=rsvp`;
  const loginUrl = `/login?redirect=/activity/${id}`;

  return (
    <div className="preview-page">
      {/* Minimal nav */}
      <nav className="preview-nav">
        <span className="preview-logo" onClick={() => navigate('/')}>COVO</span>
        <div className="preview-nav-actions">
          <button className="preview-btn-ghost" onClick={() => navigate(loginUrl)}>Log in</button>
          <button className="preview-btn-cta" onClick={() => navigate(registerUrl)}>Get started</button>
        </div>
      </nav>

      <div className="preview-body">
        {loading ? (
          <div className="preview-loading">Loading…</div>
        ) : notFound ? (
          <div className="preview-not-found">
            <h2>Activity not found</h2>
            <p>This plan may have been removed or made private.</p>
            <button className="preview-btn-cta" onClick={() => navigate('/discover')}>Browse public plans →</button>
          </div>
        ) : (
          <>
            <div className="preview-card">
              {/* Activity header */}
              <div className="preview-card-header">
                <div className="preview-type-icon">
                  <ActivityIcon type={activity.activity_type} />
                </div>
                <div>
                  <h1 className="preview-title">{activity.title || activity.activity_type}</h1>
                  <p className="preview-host">hosted by {activity.creator_display_name || 'someone'}</p>
                </div>
              </div>

              {/* Details */}
              <div className="preview-details">
                {activity.start_time && (
                  <div className="preview-detail-row">
                    <Calendar size={15} color="#52AAD8" />
                    <span>{formatDate(activity.start_time)}</span>
                  </div>
                )}
                {activity.location_name && (
                  <div className="preview-detail-row">
                    <MapPin size={15} color="#52AAD8" />
                    <span>{activity.location_name}</span>
                  </div>
                )}
                {activity.duration_minutes && (
                  <div className="preview-detail-row">
                    <Clock size={15} color="#52AAD8" />
                    <span>{activity.duration_minutes} min</span>
                  </div>
                )}
                {going.length > 0 && (
                  <div className="preview-detail-row">
                    <Users size={15} color="#52AAD8" />
                    <span><strong>{going.length}</strong> {going.length === 1 ? 'person' : 'people'} going</span>
                  </div>
                )}
              </div>

              {activity.description && (
                <p className="preview-description">{activity.description}</p>
              )}

              {/* Blurred RSVP preview */}
              {going.length > 0 && (
                <div className="preview-going-strip">
                  {going.slice(0, 4).map((r, i) => (
                    <div key={i} className="preview-going-avatar">{getInitials(r.display_name || '?')}</div>
                  ))}
                  {going.length > 4 && <div className="preview-going-avatar preview-going-more">+{going.length - 4}</div>}
                  <span className="preview-going-label">already going</span>
                </div>
              )}
            </div>

            {/* CTA banner */}
            <div className="preview-cta-card">
              <h2 className="preview-cta-title">Join to RSVP</h2>
              <p className="preview-cta-body">
                Create a free Covo account to RSVP, chat with the group, and coordinate plans with your crew.
              </p>
              <div className="preview-cta-actions">
                <button className="preview-cta-primary" onClick={() => navigate(registerUrl)}>
                  Join Covo — it's free →
                </button>
                <button className="preview-cta-secondary" onClick={() => navigate(loginUrl)}>
                  Already have an account
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
